import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  HostListener,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideSettings,
  lucideFolder,
  lucideChartLine,
  lucideMapPin,
  lucideLayoutDashboard,
  lucideEllipsis,
  lucideChevronRight,
  lucideChevronDown,
  lucideSun,
  lucideLayers,
  lucideZap,
  lucideBatteryCharging,
  lucideFileCog,
  lucideRocket,
  lucidePanelLeft,
  lucideCode,
  lucideArrowLeft,
} from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmAvatarModule } from '@shared/spartan/ui-avatar-helm/src';
import { SidebarService } from '@shared/services/sidebar.service';
import { NavSectionsService, SidebarItem } from '@shared/services/nav-sections.service';
import { Avatar } from '@shared/components/avatar/avatar';
import { User } from '@core/user/services/user';
import { UserAccountMenu } from '@shared/components/user-account-menu/user-account-menu';
import { OrganizationSelectorComponent } from '@features/organizations/components/organization-selector/organization-selector';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';

export type { SidebarItem, SidebarSection } from '@shared/services/nav-sections.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgIconComponent,
    HlmButtonDirective,
    HlmAvatarModule,
    Avatar,
    UserAccountMenu,
    OrganizationSelectorComponent,
  ],
  providers: [
    provideIcons({
      lucideLayoutDashboard,
      lucideFolder,
      lucideChartLine,
      lucideMapPin,
      lucideSettings,
      lucideEllipsis,
      lucideChevronDown,
      lucideChevronRight,
      lucideSun,
      lucideLayers,
      lucideZap,
      lucideBatteryCharging,
      lucideFileCog,
      lucideRocket,
      lucidePanelLeft,
      lucideCode,
      lucideArrowLeft,
    }),
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  sidebarService = inject(SidebarService);
  private navSections = inject(NavSectionsService);
  private user = inject(User);
  private currentOrgStore = inject(CurrentOrganizationStore);

  // Dropdown state
  isOpen = signal(false);

  constructor() {
    // Auto-restore last selected organization on user pages
    effect(() => {
      const isOnUserPage = this.navSections.isOnUserPage();
      const currentOrg = this.currentOrgStore.organization();
      const isLoading = this.currentOrgStore.isLoading();

      if (isOnUserPage && !currentOrg && !isLoading) {
        const persistedOrgId = this.currentOrgStore.getPersistedOrganizationId();
        if (persistedOrgId) {
          this.currentOrgStore
            .switchToOrganization(persistedOrgId, false)
            .catch((error) => {
              console.warn('Failed to restore last organization:', error);
              this.currentOrgStore.clearPersistedOrganization();
            });
        }
      }
    });
  }

  get isCollapsed() {
    return this.sidebarService.isCollapsed;
  }

  sections = this.navSections.sections;
  isOnUserPage = this.navSections.isOnUserPage;

  // Loading state for organization
  isOrganizationLoading = computed(() => this.currentOrgStore.isLoading());

  get userName() {
    return this.user.getName()?.trim() || this.user.getEmail()?.split('@')[0] || 'Benutzerkonto';
  }

  get userEmail() {
    return this.user.getEmail() || 'Profil wird geladen…';
  }

  get organizationInitials(): string {
    const currentOrg = this.currentOrgStore.organization();
    if (!currentOrg) {
      return '?'; // Fallback while loading
    }
    return currentOrg.name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  organizationColor = computed(() => {
    const color = this.currentOrgStore.organization()?.settings?.branding?.colors?.primary;
    return color && /^#[0-9a-f]{6}$/i.test(color) ? color : 'var(--ks-navy)';
  });

  toggleSidebar() {
    this.sidebarService.toggleSidebar();
  }

  onResizeStart(event: PointerEvent) {
    if (event.button !== 0) return;
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.sidebarService.isResizing.set(true);
  }

  onResizeMove(event: PointerEvent) {
    if (this.sidebarService.isResizing()) this.sidebarService.resizeTo(event.clientX);
  }

  onResizeEnd(event: PointerEvent) {
    if (!this.sidebarService.isResizing()) return;
    this.sidebarService.resizeTo(event.clientX);
    this.sidebarService.isResizing.set(false);
  }

  onResizeCancel() {
    this.sidebarService.isResizing.set(false);
  }

  onResizeKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') {
      if (this.isCollapsed()) this.toggleSidebar();
      else this.sidebarService.resizeTo(this.sidebarService.width() + 16);
    } else if (event.key === 'ArrowLeft') {
      this.sidebarService.resizeTo(this.sidebarService.width() - 16);
    } else if (event.key === 'Home') {
      this.sidebarService.resizeTo(this.sidebarService.collapsedWidth);
    } else if (event.key === 'End') {
      this.sidebarService.resizeTo(this.sidebarService.maxWidth);
    } else {
      return;
    }
    event.preventDefault();
  }

  toggleItem(item: SidebarItem) {
    if (item.children) {
      item.expanded = !item.expanded;
    }
  }

  handleItemClick(item: SidebarItem) {
    if (item.children) {
      this.toggleItem(item);
    }
    // If item has a route, the routerLink will handle navigation
  }

  // Dropdown Control Methods
  toggleDropdown(): void {
    this.isOpen.update((open) => !open);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Element;
    if (!target.closest('[data-dropdown-container="user-menu"]') && this.isOpen()) {
      this.closeDropdown();
    }
  }
}
